import { UUID } from "io-ts-types/UUID";
import { useEffect } from "react";

import { NpsUserKind } from "@every.org/common/src/routes/nps";

import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import {
  NpsActionType,
  dispatchNpsAction,
  useNpsContext,
} from "src/context/NpsContext";
import { submitNpsShown } from "src/context/NpsContext/actions";
import { useUserExperiment } from "src/utility/abtesting";

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const DONOR_DELAY_MS = 3_000;
const ADMIN_DELAY_MS = 60_000;

export function useShowNps(nonprofitId?: UUID, enabled = true): void {
  const loggedInUser = useLoggedInUserOrUndefined();
  const { lastNpsShownAt } = useNpsContext();

  const isAdminForNonprofit =
    nonprofitId != null &&
    (loggedInUser?.adminFor ?? []).some(
      (entry) => entry.nonprofit.id === nonprofitId
    );
  const isAnyAdmin = (loggedInUser?.adminFor ?? []).length > 0;

  const cooldownPassed =
    lastNpsShownAt !== undefined &&
    (!lastNpsShownAt ||
      Date.now() - new Date(lastNpsShownAt).getTime() >= NINETY_DAYS_MS);

  const eligible =
    enabled &&
    !!loggedInUser &&
    cooldownPassed &&
    !(nonprofitId != null && !isAdminForNonprofit);

  const npsExperiment = useUserExperiment("nps_modal", !eligible);
  const npsVisible = npsExperiment.get("visible", false);

  useEffect(() => {
    if (!eligible || !npsVisible) {
      return;
    }

    const userKind =
      isAdminForNonprofit || isAnyAdmin ? NpsUserKind.ADMIN : NpsUserKind.DONOR;

    const delay = isAdminForNonprofit ? ADMIN_DELAY_MS : DONOR_DELAY_MS;

    const timer = setTimeout(() => {
      dispatchNpsAction({
        type: NpsActionType.SHOW_NPS,
        userKind,
        nonprofitId: nonprofitId ?? null,
      });
      submitNpsShown({ userKind, nonprofitId: nonprofitId ?? null });
    }, delay);

    return () => clearTimeout(timer);
  }, [eligible, npsVisible, isAdminForNonprofit, isAnyAdmin, nonprofitId]);
}
